import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Target, Users } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Sign In State
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  // Sign Up State
  const [signUpName, setSignUpName] = useState('');
  const [signUpCompany, setSignUpCompany] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpRole, setSignUpRole] = useState('manager');

  // Check for Google OAuth callback
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const params = new URLSearchParams(hash.substring(1));
      const googleSessionId = params.get('session_id');
      if (googleSessionId) {
        setSessionId(googleSessionId);
        setShowRoleSelection(true);
        // Clear hash from URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Empty deps intentional - only check on mount

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRoleSelection = async (selectedRole) => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const requestBody = {
        session_id: sessionId,
        role: selectedRole
      };

      // For managers creating NEW accounts, ask for company name
      // Backend will determine if user is new or existing
      if (selectedRole === 'manager') {
        const companyName = prompt('Enter your company name (leave empty if you already have an account):');
        if (companyName && companyName.trim()) {
          requestBody.company_name = companyName.trim();
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Google authentication failed');
      }

      login(data.token, data.user);
      setShowRoleSelection(false);
      toast({
        title: 'Welcome!',
        description: `Successfully signed in with Google as ${selectedRole}`,
      });
      
      // Navigate based on role
      if (data.user.role === 'manager') {
        navigate('/dashboard');
      } else {
        navigate('/training');
      }
    } catch (error) {
      toast({
        title: 'Authentication Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Important: send/receive cookies
        body: JSON.stringify({ email: signInEmail, password: signInPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      login(data.token, data.user);
      toast({
        title: 'Welcome back!',
        description: `Successfully signed in to AgentIQ`,
      });
      
      // Navigate based on role
      if (data.user.role === 'manager') {
        navigate('/dashboard');
      } else {
        navigate('/training');
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid email or password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!signUpName || !signUpCompany || !signUpEmail || !signUpPassword) {
        throw new Error('Please fill in all fields');
      }

      if (signUpPassword.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Important: send/receive cookies
        body: JSON.stringify({
          email: signUpEmail,
          password: signUpPassword,
          name: signUpName,
          company_name: signUpCompany,
          role: signUpRole  // Use selected role
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      login(data.token, data.user);
      toast({
        title: 'Account created!',
        description: 'Welcome to AgentIQ',
      });
      navigate('/training');
    } catch (error) {
      toast({
        title: 'Signup Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Role Selection Modal for Google OAuth */}
      <Dialog open={showRoleSelection} onOpenChange={setShowRoleSelection}>
        <DialogContent className="bg-[#1a1b23] border-[#2a2d3a]">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Select Your Role</DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose how you'll be using AgentIQ
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card 
              className="bg-[#161920] border-[#2a3040] hover:border-[#4f8ef7] cursor-pointer transition-all"
              onClick={() => handleRoleSelection('manager')}
            >
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-[#4f8ef7]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Manager</h3>
                <p className="text-xs text-gray-400">
                  Manage team, create SOPs, review QA scores
                </p>
              </CardContent>
            </Card>
            <Card 
              className="bg-[#161920] border-[#2a3040] hover:border-[#34c78a] cursor-pointer transition-all"
              onClick={() => handleRoleSelection('agent')}
            >
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-[#34c78a]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Agent</h3>
                <p className="text-xs text-gray-400">
                  Take training, improve skills, track progress
                </p>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img 
              src="/agentiq-icon.png" 
              alt="AgentIQ Logo" 
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-2xl font-semibold text-white">AgentIQ</h1>
          </div>
        </div>

        <Card className="bg-[#1a1b23] border-[#2a2d3a]">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-xl">Welcome back</CardTitle>
            <CardDescription className="text-gray-400">
              Sign in to your team account to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-[#13141a] mb-6">
                <TabsTrigger value="signin" className="data-[state=active]:bg-[#6B9EFF]/10 data-[state=active]:text-[#6B9EFF]">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-[#6B9EFF]/10 data-[state=active]:text-[#6B9EFF]">
                  Create Account
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Email</label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={signInEmail}
                      onChange={(e) => setSignInEmail(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Password</label>
                    <Input
                      type="password"
                      placeholder="Your password"
                      value={signInPassword}
                      onChange={(e) => setSignInPassword(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#6B9EFF] hover:bg-[#5a8eef] text-white"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-[#2a2d3a]" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-[#1a1b23] px-2 text-gray-500">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#2a2d3a] text-white hover:bg-[#161920]"
                    onClick={handleGoogleLogin}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign in with Google
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Your Name</label>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={signUpName}
                      onChange={(e) => setSignUpName(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Company Name</label>
                    <Input
                      type="text"
                      placeholder="Acme Corp"
                      value={signUpCompany}
                      onChange={(e) => setSignUpCompany(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">I am a...</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant={signUpRole === 'manager' ? 'default' : 'outline'}
                        className={signUpRole === 'manager' 
                          ? 'bg-[#4f8ef7] text-white' 
                          : 'bg-[#13141a] border-[#2a2d3a] text-gray-400 hover:text-white'
                        }
                        onClick={() => setSignUpRole('manager')}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Manager
                      </Button>
                      <Button
                        type="button"
                        variant={signUpRole === 'agent' ? 'default' : 'outline'}
                        className={signUpRole === 'agent' 
                          ? 'bg-[#34c78a] text-white' 
                          : 'bg-[#13141a] border-[#2a2d3a] text-gray-400 hover:text-white'
                        }
                        onClick={() => setSignUpRole('agent')}
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Agent
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Work Email</label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={signUpEmail}
                      onChange={(e) => setSignUpEmail(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Password (min 8 characters)</label>
                    <Input
                      type="password"
                      placeholder="Create a strong password"
                      value={signUpPassword}
                      onChange={(e) => setSignUpPassword(e.target.value)}
                      className="bg-[#13141a] border-[#2a2d3a] text-white placeholder:text-gray-500"
                      required
                      minLength={8}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#6B9EFF] hover:bg-[#5a8eef] text-white"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-[#2a2d3a]" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-[#1a1b23] px-2 text-gray-500">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#2a2d3a] text-white hover:bg-[#161920]"
                    onClick={handleGoogleLogin}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign up with Google
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-xs mt-6">
          Demo credentials: Any email/password combination will work
        </p>
      </div>
    </div>
    </>
  );
};

export default Login;
